import { ApiError, setAccountTransport } from './api';

declare const __MARVIN_WEB_BUILD__: string;
export const accountWebBuild = __MARVIN_WEB_BUILD__;
type Reply = {
  channel: string;
  event?: string;
  id: string;
  ok: boolean;
  value?: unknown;
  error?: string;
  status?: number;
};
type WebView = {
  postMessage: (message: unknown) => void;
  addEventListener: (event: string, listener: (event: MessageEvent<Reply>) => void) => void;
};
const webview = (window as unknown as { chrome?: { webview?: WebView } }).chrome?.webview;
const pending = new Map<
  string,
  {
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout> | null;
  }
>();
webview?.addEventListener('message', ({ data }) => {
  if (data?.channel === 'marvin-account-v1' && data.event === 'appearance') {
    window.dispatchEvent(new CustomEvent('marvin-host-appearance', { detail: data.value }));
    return;
  }
  if (data?.channel !== 'marvin-account-v1' || typeof data.id !== 'string') return;
  const request = pending.get(data.id);
  if (!request) return;
  if (request.timer !== null) clearTimeout(request.timer);
  pending.delete(data.id);
  if (data.ok) request.resolve(data.value);
  else
    request.reject(
      new ApiError(
        data.error || 'The account request failed. Please try again.',
        data.status || 500,
      ),
    );
});
export function desktopAccountRequest<T>(operation: string, body?: unknown): Promise<T> {
  if (!webview)
    return Promise.reject(new Error('Open these account settings from the Marvin desktop app.'));
  return new Promise((resolve, reject) => {
    const id = crypto.randomUUID();
    // Native passkey management is a user-controlled dialog, not a network
    // request. It may legitimately remain open beyond the request deadline.
    const timer = ['openPasskeys', 'dictateProfile'].includes(operation) ? null : setTimeout(() => {
      pending.delete(id);
      reject(new Error('The account request timed out. Check your connection and retry.'));
    }, 120000);
    pending.set(id, { resolve: (value) => resolve(value as T), reject, timer });
    webview.postMessage({
      channel: 'marvin-account-v1',
      protocol: 1,
      id,
      operation,
      body: body ?? {},
      webBuild: accountWebBuild,
    });
  });
}
const operations: Record<string, string> = {
  'GET /account/session': 'session',
  'PATCH /account/profile': 'profile',
  'POST /account/email': 'changeEmail',
  'POST /account/verify-email': 'verifyEmail',
  'POST /account/verification-code': 'resendCode',
  'POST /account/password': 'changePassword',
  'POST /account/logout': 'signOut',
  'DELETE /account': 'deleteAccount',
  'POST /input-pastes': 'inputPaste',
};
export function installDesktopAccountTransport() {
  setAccountTransport(async (path, method, body) => {
    const operation = operations[`${method} ${path}`];
    if (!operation) throw new Error('This action is not available in desktop account settings.');
    return desktopAccountRequest(operation, body);
  });
}
